// ============================================
// CARTDROPDOWN.JS - CONECTADO A CARTCONTEXT
// ============================================

import React, { useEffect, useRef } from "react";
import { useCart } from "../../context/CartContext";
import { lockScroll, unlockScroll } from "../../utils/scrollLock";
import CartList from "./CartList";
import CartSum from "./CartSum";
import "./CartDropdown.css";

// SVG del botón cerrar
const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

export default function CartDropdown({ isOpen, onClose }) {
  const { cartItems, cartCount, subtotal, shipping, total, updateQuantity, removeItem } = useCart();
  const contentRef = useRef(null);
  const savedScrollY = useRef(0);
  const panelHeight = Math.min(640, Math.round(window.innerHeight * 0.85));

  // Scroll lock + Scroll to top cuando se abre el cart
  useEffect(() => {
    if (isOpen) {
      savedScrollY.current = window.scrollY;

      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });

      const lockScrollAfterAnimation = setTimeout(() => {
        document.body.classList.add('phys-cart-open');
        lockScroll();
      }, 400);

      return () => clearTimeout(lockScrollAfterAnimation);
    } else {
      document.body.classList.remove('phys-cart-open');
      unlockScroll();

      if (savedScrollY.current > 0) {
        setTimeout(() => {
          window.scrollTo(0, savedScrollY.current);
        }, 100);
      }
    }

    return () => {
      document.body.classList.remove('phys-cart-open');
      unlockScroll();
    };
  }, [isOpen]);

  // Cerrar con ESC
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Manejar cambio de cantidad
  const handleUpdateQuantity = (itemId, newQuantity) => {
    updateQuantity(itemId, newQuantity);
  };

  // Manejar eliminación
  const handleRemove = (itemId) => {
    removeItem(itemId);
  };

  return (
    <>
      {/* Overlay */}
      <div 
        className={`cart-pushdown-overlay ${isOpen ? 'is-open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Cart Container */}
      <div 
        className={`cart-pushdown-wrapper ${isOpen ? 'is-open' : ''}`}
        style={{ maxHeight: isOpen ? `${panelHeight}px` : '0px' }}
      >
        {/* Altura fija = la del panel: header y resumen fijos, solo la lista
            hace scroll. Así el botón para ir al carrito nunca queda tapado. */}
        <div ref={contentRef} className="cart-pushdown-content" style={{ maxHeight: `${panelHeight}px` }}>
          
          {/* HEADER */}
          <div className="cart-header-compact">
            <div className="cart-header-left">
              <h2 className="cart-title-clean">Mi carrito ({cartCount})</h2>
            </div>
            
            <div className="cart-columns-inline">
              <span className="col-label">PRODUCTO</span>
              <span className="col-label">TAMAÑO</span>
              <span className="col-label">CANTIDAD</span>
            </div>
            
            <div className="cart-header-price-spacer"></div>
            
            <button 
              className="cart-close-clean"
              onClick={onClose}
              aria-label="Cerrar carrito"
            >
              <CloseIcon />
            </button>
          </div>

          {/* Lista de productos */}
          <div className="cart-items-clean">
            {cartItems && cartItems.length > 0 ? (
              cartItems.map((item) => (
                <CartList
                  key={item.id}
                  item={{
                    id: item.id,
                    product: item.name,
                    size: item.size,
                    image: item.image,
                    price: item.price,
                    quantity: item.quantity
                  }}
                  onUpdateQuantity={handleUpdateQuantity}
                  onRemove={handleRemove}
                />
              ))
            ) : (
              <div className="empty-cart-clean">
                <p>Tu carrito está vacío</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <CartSum 
            subtotal={subtotal}
            shipping={shipping}
            total={total}
            onClose={onClose}
          />
        </div>
      </div>
    </>
  );
}
